import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CookiePreferences, CookieCategory } from '../types';
import { 
  getCookiePreferences, 
  saveCookiePreferences, 
  hasCookieConsent 
} from '../lib/cookieUtils';

interface CookieContextType {
  preferences: CookiePreferences;
  hasConsent: boolean;
  showBanner: boolean;
  showSettings: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  acceptSelected: (preferences: CookiePreferences) => void;
  openSettings: () => void;
  closeSettings: () => void;
  updateCategory: (category: CookieCategory, enabled: boolean) => void;
}

const CookieContext = createContext<CookieContextType | undefined>(undefined);

interface CookieProviderProps {
  children: ReactNode;
}

export function CookieProvider({ children }: CookieProviderProps) {
  const [preferences, setPreferences] = useState<CookiePreferences>(getCookiePreferences());
  const [hasConsent, setHasConsent] = useState<boolean>(hasCookieConsent());
  const [showBanner, setShowBanner] = useState<boolean>(!hasCookieConsent());
  const [showSettings, setShowSettings] = useState<boolean>(false);

  // 初始化时检查是否已有同意
  useEffect(() => {
    const consent = hasCookieConsent();
    setHasConsent(consent);
    setShowBanner(!consent);
  }, []);

  // 接受所有cookie
  const acceptAll = () => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      consentGiven: true,
      consentDate: new Date().toISOString(),
    };
    saveCookiePreferences(newPreferences);
    setPreferences(newPreferences);
    setHasConsent(true);
    setShowBanner(false);
    setShowSettings(false);
  };

  // 拒绝所有非必要cookie
  const rejectAll = () => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      consentGiven: true,
      consentDate: new Date().toISOString(),
    };
    saveCookiePreferences(newPreferences);
    setPreferences(newPreferences);
    setHasConsent(true);
    setShowBanner(false);
    setShowSettings(false);
  };

  // 接受选定的cookie类别
  const acceptSelected = (newPreferences: CookiePreferences) => {
    saveCookiePreferences(newPreferences);
    setPreferences(newPreferences);
    setHasConsent(true);
    setShowBanner(false);
    setShowSettings(false);
  };

  // 打开设置面板
  const openSettings = () => {
    setShowSettings(true);
  };

  // 关闭设置面板
  const closeSettings = () => {
    setShowSettings(false);
  };

  // 更新单个cookie类别
  const updateCategory = (category: CookieCategory, enabled: boolean) => {
    // 必要cookie不能禁用
    if (category === 'necessary') {
      return;
    }

    const newPreferences: CookiePreferences = {
      ...preferences,
      [category]: enabled,
    };
    setPreferences(newPreferences);
  };

  const value: CookieContextType = {
    preferences,
    hasConsent,
    showBanner,
    showSettings,
    acceptAll,
    rejectAll,
    acceptSelected,
    openSettings,
    closeSettings,
    updateCategory,
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
}

export function useCookie(): CookieContextType {
  const context = useContext(CookieContext);
  if (context === undefined) {
    throw new Error('useCookie必须在CookieProvider内使用');
  }
  return context;
}

